import React from 'react';
import { isEmpty } from 'lodash';
import { node, array, object, bool, func } from 'prop-types';
import UserOutcomesContext from 'apps/journey/context/user-outcomes-context';
import withData from 'util/hoc/with-data';
import withUser from 'util/hoc/with-user';
import withEntity from 'util/hoc/with-entity';
import 'styles/components/ellipses-loading.scss';
import { userOutcomesQuery } from 'apps/journey/queries/userOutcomesQuery';
import { getRecommendedOutcomes } from 'apps/journey/queries/recommendedOutcomesQuery';
import { resolve } from 'library/helpers/path-resolver';

const SortParamMap = {
  created_at: 'created_at',
  due_date: 'due_date',
  added_by: 'user_referrer/first_name',
};

const getCompareFn = (extraParams) => {
  if (!isEmpty(extraParams) && extraParams.by_user_outcome_status) {
    return (a, b) => {
      let ca = false;
      let cb = false;
      if (extraParams.by_user_outcome_status === 'not_started') {
        ca = !a.accepted_at;
        cb = !b.accepted_at;
      } else if (extraParams.by_user_outcome_status === 'dismissed') {
        ca = !!a.archived;
        cb = !!b.archived;
      } else if (extraParams.by_user_outcome_status === 'in_progress') {
        ca = 0;
        a?.goals?.forEach((eg) => {
          if (eg.steps) {
            eg.steps.forEach((es) => {
              if (es.complete) {
                ca++;
              }
            });
          }
        });
        cb = 0;
        b?.goals?.forEach((eg) => {
          if (eg.steps) {
            eg.steps.forEach((es) => {
              if (es.complete) {
                cb++;
              }
            });
          }
        });
      } else if (extraParams.by_user_outcome_status === 'completed') {
        ca = 0;
        a?.goals?.forEach((eg) => {
          if (eg.completed_at) {
            ca++;
          }
        });
        cb = 0;
        b?.goals?.forEach((eg) => {
          if (eg.completed_at) {
            cb++;
          }
        });
      }
      if (extraParams.order === 'asc') {
        return ca >= cb ? 1 : -1;
      } else {
        return ca <= cb ? 1 : -1;
      }
    };
  }

  const sortParamName = !isEmpty(extraParams)
    ? SortParamMap[extraParams?.sort_by]
    : SortParamMap.created_at;

  return (a, b) => {
    const defaultSortValue =
      sortParamName === 'user_referrer/first_name' ? 'System' : false;
    const ca = resolve(a, sortParamName, defaultSortValue);
    const cb = resolve(b, sortParamName, defaultSortValue);
    if (isEmpty(extraParams) || extraParams?.order === 'asc') {
      if (ca && cb) {
        return ca >= cb ? 1 : -1;
      } else if (ca && !cb) {
        return -1;
      } else if (!ca && cb) {
        return 1;
      } else {
        return 0;
      }
    } else {
      if (ca && cb) {
        return ca <= cb ? 1 : -1;
      } else if (ca && !cb) {
        return -1;
      } else if (!ca && cb) {
        return 1;
      } else {
        return 0;
      }
    }
  };
};

const UserOutcomesWrapper = ({
  children,
  graph_loading,
  graph_data,
  reload_graph,
  searchRow,
  ...props
}) => {
  if (graph_loading && searchRow) {
    return (
      <div className="body">
        <div className="ellipses-loading" />
      </div>
    );
  }

  const { allOutcomes = [], recommendedOutcomes = [] } = graph_data;

  const contextParams = {
    outcomes: allOutcomes,
    reloadOutcomes: reload_graph,
    recommendedOutcomes: recommendedOutcomes.filter((x) => !x.accepted_at),
    reloadRecommendations: reload_graph,
    outcomes_loading: graph_loading,
  };

  return (
    <UserOutcomesContext.Provider value={contextParams}>
      {children}
    </UserOutcomesContext.Provider>
  );
};

UserOutcomesWrapper.propTypes = {
  children: node,
  outcomes: array,
  graph_loading: bool,
  graph_data: object,
  reload_graph: func,
  user: object,
  entity: object,
  searchRow: bool,
};

UserOutcomesWrapper.showQueryError = false;
UserOutcomesWrapper.dependantPropPath = 'user/uuid';
UserOutcomesWrapper.query = async (
  cancelToken,
  { user, entity },
  extraParams
) => {
  return await Promise.all([
    await getRecommendedOutcomes({
      user: user,
      entity: entity,
      sort_by: extraParams?.sort_by,
      order: extraParams?.order,
      by_outcome_name: extraParams?.by_outcome_name,
      by_user_outcome_status: extraParams?.by_user_outcome_status,
    }),
    await userOutcomesQuery(user.uuid),
  ]).then(([allRecommendations, userOutcomes]) => {
    const outcomes = userOutcomes?.data.user.outcomes || [];
    const recommendedOutcomes = (
      allRecommendations.data?.all_recommendations?.results || []
    ).reduce((memo, current) => {
      const userOutcome = outcomes.find((x) => x.id === current.outcome.id);
      if (userOutcome) {
        const userGoals = userOutcome.goals.filter((x) =>
          current.outcome.goals.some((y) => y.id === x.id)
        );
        memo.push({
          ...current.outcome,
          recommendationId: current.id,
          due_date: current.due_date,
          user_referrer: current.user_referrer,
          accepted_at: current.accepted_at,
          archived: userOutcome.archived,
          updated_at: current.updated_at,
          created_at: current.created_at,
          goals: userGoals,
        });
      } else {
        memo.push({
          ...current.outcome,
          recommendationId: current.id,
          due_date: current.due_date,
          user_referrer: current.user_referrer,
          accepted_at: current.accepted_at,
          updated_at: current.updated_at,
          created_at: current.created_at,
        });
      }
      return memo;
    }, []);

    const systemOutcomes = outcomes.reduce((memo, current) => {
      const foundItem = recommendedOutcomes.some((x) => x.id === current.id);
      if (!foundItem) {
        memo.push({
          ...current,
          accepted_at: true,
        });
      }
      return memo;
    }, []);

    const allOutcomes = [...recommendedOutcomes, ...systemOutcomes];

    const compareFn = getCompareFn(extraParams);

    const filteredSortedList =
      systemOutcomes.length > 0
        ? allOutcomes
            .filter(
              (x) =>
                isEmpty(extraParams?.by_outcome_name) ||
                x?.name
                  ?.toString()
                  .toLowerCase()
                  ?.indexOf(
                    extraParams?.by_outcome_name?.toString().toLowerCase()
                  ) > -1
            )
            .sort(compareFn)
        : allOutcomes;

    return {
      data: {
        allOutcomes: filteredSortedList,
        recommendedOutcomes,
      },
    };
  });
};

export default withEntity(withUser(withData(UserOutcomesWrapper)));
